import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { getPatientDetailsByLink, CallApiForLoginCheck, CallApiForOTPVerification, getBrowserInfo, getUserIp, CallApiToMarkLinkOpened } from "../helpers/http";
import "../styles/Login.css";
import { IconUser, IconMail, IconPhone, IconShield, IconBack, IconUserAvatar } from "../Component/shared/icons/Index";
import LoginDatePicker from "../Component/shared/loginDatePicker/Index";
import { getSessionData } from "../helpers/Util";
import ManualLogin from "./ManualLogin";
import PatientRegistration from "./patientRegistration.js/PatientRegistration";
import { CheckPatientRegistration, RegisterPatient } from "./patientRegistration.js/service";

// TOKEN VERIFYING SCREEN

const VerifyingScreen = () => (
  <div className="login-page">
    <div className="login-card">
      <div className="login-top-bar login-top-bar--verifying" />
      <div className="login-header">
        <div className="login-avatar-box"><IconUserAvatar /></div>
        <div className="login-header-text">
          <span className="login-logo-text-bold">GREEN <span className="login-logo-text-light">BILLS</span></span>
          <span className="login-subtitle">Medical Centre</span>
        </div>
      </div>
      <div className="tl-icon-wrap">
        <div className="tl-icon-circle">
          <div className="tl-spin-ring-outer" />
          <div className="tl-spin-ring-inner" />
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#2d8a5e" strokeWidth="1.8" strokeLinejoin="round" />
            <path d="M2 6l10 7 10-7" stroke="#2d8a5e" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
      <h2 className="login-heading" style={{ textAlign: "center", marginBottom: 6 }}>Verifying your link</h2>
      <p className="login-subheading" style={{ textAlign: "center", marginBottom: 24 }}>
        We're confirming your identity from the link. This only takes a moment.
      </p>
      <div className="tl-steps">
        <div className="tl-step tl-step--done">
          <div className="tl-step-icon tl-step-icon--done">
            <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
              <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          Link received
        </div>
        <div className="tl-step tl-step--active">
          <div className="tl-step-icon tl-step-icon--active">
            <div className="tl-step-spinner" />
          </div>
          Validating the link
        </div>
      </div>
      <p className="login-footer" style={{ marginTop: 20 }}><IconShield />Your information is encrypted and secure.</p>
      
    </div>
  </div>
);


// TOKEN FAILED SCREEN
const FailedScreen = ({ onRetry }) => (
  <div className="login-page">
    <div className="login-card">
      <div className="login-top-bar login-top-bar--failed" />
      <div className="login-header">
        <div className="login-avatar-box"><IconUserAvatar /></div>
        <div className="login-header-text">
          <span className="login-logo-text-bold">GREEN <span className="login-logo-text-light">BILLS</span></span>
          <span className="login-subtitle">Medical Centre</span>
        </div>
      </div>
      <div className="tl-icon-wrap">
        <div className="tl-icon-circle tl-icon-circle--failed">
          <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
            <circle cx="22" cy="22" r="22" fill="rgba(239,68,68,0.1)" />
            <path className="tl-x-path" d="M15 15l14 14M29 15L15 29" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" />
          </svg>
        </div>
      </div>
      <h2 className="login-heading" style={{ textAlign: "center", marginBottom: 6 }}>Link expired or invalid</h2>
      <p className="login-subheading" style={{ textAlign: "center", marginBottom: 16 }}>
        We couldn't verify this link. It may have expired.
      </p>
      <div className="tl-reason-card">
        <div className="tl-reason-title">Why this happens</div>
        <div className="tl-reason-item"><div className="tl-reason-dot" />Link expires after 24 hours.</div>
        <div className="tl-reason-item"><div className="tl-reason-dot" />Opening in a different browser or device.</div>
        <div className="tl-reason-item"><div className="tl-reason-dot" />Link was copied incorrectly from email.</div>
      </div>
      <button className="tl-btn-continue_login" onClick={onRetry}>
        Try Another Way
      </button>
    </div>
  </div>
);

// MAIN LOGIN COMPONENT
const Login = () => {
  const navigate = useNavigate();

  const [tokenStatus, setTokenStatus] = useState("verifying");
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(false);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [formData, setFormData] = useState({
    firstName: "", lastName: "", dateOfBirth: "", email: "", phoneNumber: "",
  });
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [focused, setFocused] = useState({});
  const [countdown, setCountdown] = useState(0);
  const [isPreFilled, setIsPreFilled] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [showManualLogin, setShowManualLogin] = useState(false);
  const [showRegisterPage, setShowRegisterPage] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [dob, setDob] = useState('');

  const inputRefs = useRef([]);
  const timerRef = useRef(null);

  const RequiredStar = () => (
    <span style={{ color: "#e05252", marginLeft: 3, fontWeight: 700 }}>*</span>
  );

  const params = new URLSearchParams(window.location.search);
  const id = params.get("id");

  useEffect(() => {
    sessionStorage.clear();
  }, []);

  useEffect(() => {
    if (id) {
      setTokenStatus("verifying");
      LoadIntakeLink(id);
    } else {
      setTokenStatus("idle");
    }
  }, [id]);

  useEffect(() => { return () => clearInterval(timerRef.current); }, []);

  const LoadIntakeLink = async (id) => {
    await getUserIp();
    getBrowserInfo();
    try {
      const res = await getPatientDetailsByLink(id);

      if (res?.success) {
        localStorage.setItem("tokenUrl", window.location.href);
        const data = res?.data?.data || {};

        let companyId = data?.companyId;

        sessionStorage.setItem("tokenData", JSON.stringify({
          ...data,
          companyId,
          caseId: data?.caseId || data?.CaseId || "",
        }));

        const parseTokenArray = (val) => {
          if (!val) return [];
          if (Array.isArray(val)) return val;
          try { return JSON.parse(val); } catch { return []; }
        };

        sessionStorage.setItem("tokenNodes", JSON.stringify(parseTokenArray(data?.nodes || data?.Nodes)));
        sessionStorage.setItem("tokenIntakeSheets", JSON.stringify(
          parseTokenArray(
            data?.intakeSheets ||
            data?.IntakeSheets ||
            data?.intakeSheet ||
            data?.IntakeSheet
          )
        ));

        let parsedSelectedRow = {};
        if (data.selectedRow) {
          try {
            parsedSelectedRow = JSON.parse(data.selectedRow);
          } catch (parseError) {
            console.error("Error parsing selectedRow:", parseError);
          }
        } else {
          console.warn("selectedRow is missing in response data");
        }

        sessionStorage.setItem("selectedRow", JSON.stringify(parsedSelectedRow));
        sessionStorage.setItem("tokenDocuments", JSON.stringify(parseTokenArray(data?.documents || data?.Documents)));
        sessionStorage.setItem("tokenRateReviewTemplate", JSON.stringify(
          parseTokenArray(data?.rateReviewTemplate || data?.RateReviewTemplate)[0] || null
        ));
        sessionStorage.setItem("accessApiToken", JSON.stringify(data.accessApiToken));
        sessionStorage.setItem("commonServiceToken", JSON.stringify(data.commonserviceToken));

        const requestData = {
          caseId: data?.caseId || data?.CaseId || "",
          companyId: data?.companyId || "",
          firstName: data?.firstName || "",
          lastName: data?.lastName || "",
          email: data?.email || "",
          phoneNumber: data?.cellPhone || "",
          dateOfBirth: getFormattedDob(data, true),
        };

        let isPatientRegistered = false;
        try {
          const checkRegistrationRes = await CheckPatientRegistration(requestData);
          isPatientRegistered = Boolean(
            checkRegistrationRes?.data?.isRegistered || checkRegistrationRes?.data?.data?.isRegistered
          );
        } catch (e) {
          console.error("Error checking patient registration:", e);
        }

        setIsRegistered(isPatientRegistered);
        setCompanyId(companyId || "");
        setDob(getFormattedDob(data));

        setFormData({
          caseId: data?.caseId || "",
          companyId: companyId || "",
          firstName: data?.firstName || "",
          lastName: data?.lastName || "",
          dateOfBirth: data?.dateOfBirth ? data.dateOfBirth.split("T")[0] : "",
          email: data?.email || "",
          phoneNumber: data?.cellPhone || "",
          doctorName: data?.doctorName || "",
          doctorSpecialtyName: data?.doctorSpecialtyName || "",
          providerName: data?.providerName || "",
        });

        setIsPreFilled(true);
        setTokenStatus("ok");
      } else {
        setTokenStatus("failed");
      }
    } catch (err) {
      setTokenStatus("failed");
    }
  };

  const handleRetry = () => {
    window.history.replaceState({}, "", window.location.pathname);
    // Clear form and disable pre-fill so email & phone become editable
    setIsPreFilled(false);
    setIsRegistered(false);
    setFormData({ caseId: "", firstName: "", lastName: "", dateOfBirth: "", email: "", phoneNumber: "" });
    setTokenStatus("idle");
    setApiError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    if (apiError) setApiError("");
  };

  const handleDobChange = (val) => {
    setFormData(prev => ({ ...prev, dateOfBirth: val }));
    if (errors.dateOfBirth) setErrors(prev => ({ ...prev, dateOfBirth: "" }));
    if (apiError) setApiError("");
  };

  const validateForm = () => {
    const newErrors = {};
    const { firstName, lastName, dateOfBirth, email, phoneNumber } = formData;
    if (!isRegistered) {
      if (!firstName.trim()) newErrors.firstName = "First name is required";
      if (!lastName.trim()) newErrors.lastName = "Last name is required";
      if (!dateOfBirth) newErrors.dateOfBirth = "Date of birth is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFormattedDob = (tokenData, convert = false) => {
    const isStringInput = typeof tokenData === "string";
    if (isRegistered || convert || isStringInput) {
      const dateString = isStringInput ? tokenData : (tokenData?.dob || tokenData?.dateOfBirth || "");
      if (dateString) {
        try {
          if (dateString.includes("/")) {
            const [datePart, timePart = "00:00:00"] = dateString.split(" ");
            const parts = datePart.split("/");
            let month, day, year;
            if (parts[0].length === 4) {
              // YYYY/MM/DD
              [year, month, day] = parts.map(Number);
            } else if (Number(parts[0]) > 12) {
              // DD/MM/YYYY
              day = Number(parts[0]);
              month = Number(parts[1]);
              year = Number(parts[2]);
            } else {
              // MM/DD/YYYY
              month = Number(parts[0]);
              day = Number(parts[1]);
              year = Number(parts[2]);
            }
            const timeParts = (timePart || "00:00:00").split(":").map(Number);
            return new Date(
              Date.UTC(
                year,
                month - 1,
                day,
                timeParts[0] || 0,
                timeParts[1] || 0,
                timeParts[2] || 0
              )
            ).toISOString();
          } else if (dateString.includes("-") && dateString.includes(" ")) {
            const [datePart, timePart] = dateString.split(" ");
            const [day, month, year] = datePart.split("-");
            return new Date(
              Date.UTC(
                Number(year),
                Number(month) - 1,
                Number(day),
                ...timePart.split(":").map(Number)
              )
            ).toISOString();
          } else if (dateString.includes("-") && dateString.split("-")[0].length <= 2) {
            const [day, month, year] = dateString.split("-");
            return new Date(
              Date.UTC(
                Number(year),
                Number(month) - 1,
                Number(day),
                0, 0, 0
              )
            ).toISOString();
          } else if (dateString.includes("-") && dateString.split("-")[0].length === 4) {
            const [year, month, day] = dateString.split("-");
            return new Date(
              Date.UTC(
                Number(year),
                Number(month) - 1,
                Number(day),
                0, 0, 0
              )
            ).toISOString();
          }
          return new Date(dateString).toISOString();
        } catch (e) {
          console.error("Error formatting date of birth:", e);
          return "";
        }
      }
      return "";
    }
    if (formData.dateOfBirth) {
      if (formData.dateOfBirth.includes("-") && formData.dateOfBirth.split("-")[0].length === 4) {
        const [year, month, day] = formData.dateOfBirth.split("-");
        return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 0, 0, 0)).toISOString();
      }
      return new Date(formData.dateOfBirth).toISOString();
    }
    return "";
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrors({});
    setApiError("");
    if (!validateForm()) return;
    setLoading(true);
    const tokenData = getSessionData('tokenData', {});
    const resolvedCompanyId = tokenData?.companyId || companyId || formData?.companyId || "";
    const resolvedDob = getFormattedDob(tokenData) || getFormattedDob(formData?.dateOfBirth) || dob || "";
    if (resolvedCompanyId) setCompanyId(resolvedCompanyId);
    if (resolvedDob) setDob(resolvedDob);
    try {
      const loginData = {
        caseId: formData.caseId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: resolvedDob,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        companyId: resolvedCompanyId
      };
      const res = await CallApiForLoginCheck(loginData);
      if (res?.success) {
        setResponse(true);
        toast.success("OTP sent successfully!");
        startCountdown(60);
      }
      else {
        setApiError("Patient Not Found.");
      }
    } catch (err) {
      const error = err?.message === 'Patient not found' ? 'Patient Not Found.' : 'Something went wrong.';
      setApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeOTP = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (apiError) setApiError("");
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e, index) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    e.preventDefault();
    const newOtp = [...otp];
    for (let i = 0; i < pasted.length; i++) {
      if (index + i < 6) newOtp[index + i] = pasted[i];
    }
    setOtp(newOtp);
    inputRefs.current[Math.min(index + pasted.length, 5)]?.focus();
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    const tokenData = getSessionData('tokenData', {})
    setApiError("");
    const otpValue = otp.join("");
    if (otpValue.length !== 6) { setApiError("Please enter complete 6-digit OTP."); return; }
    setLoading(true);
    try {
      const verificationData = {
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        otp: otpValue,
      };
      const res = await CallApiForOTPVerification(verificationData);
      if (res?.success) {
        await CallApiToMarkLinkOpened(id);
        if (!isRegistered) {
          const requestData = {
            caseId: tokenData?.caseId || formData.caseId || "",
            companyId: tokenData?.companyId || companyId || formData.companyId || "",
            firstName: tokenData?.firstName || formData.firstName || "",
            lastName: tokenData?.lastName || formData.lastName || "",
            email: tokenData?.email || formData.email || "",
            phoneNumber: tokenData?.cellPhone || formData.phoneNumber || "",
            dateOfBirth: getFormattedDob(tokenData, true) || getFormattedDob(formData.dateOfBirth, true) || dob,
          };
          await RegisterPatient(requestData);
        }
        toast.success("OTP verified successfully!");
        sessionStorage.setItem("otpVerified", "true");
        localStorage.setItem("userData", JSON.stringify({
          caseId: formData.caseId,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
        }));
        localStorage.setItem("patientDetails", JSON.stringify({
          DoctorName: formData.doctorName,
          DoctorSpecialtyName: formData.doctorSpecialtyName,
          ProviderName: formData.providerName,
          ...(res.patientDetails || {}),
        }));
        navigate("/patientPortal");
      } else {
        setApiError(res?.message || "OTP Verification Failed.");
        setOtp(["", "", "", "", "", ""]);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error("OTP verification error:", err);
      const error = err?.message === 'Invalid or expired OTP' ? 'Invalid or Expired OTP.' : 'Something went wrong.';
      setApiError(error);
      setOtp(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    const tokenData = getSessionData('tokenData', {});
    const resolvedCompanyId = tokenData?.companyId || companyId || formData?.companyId || "";
    const resolvedDob = getFormattedDob(tokenData) || getFormattedDob(formData?.dateOfBirth) || dob || "";
    try {
      const loginData = {
        caseId: formData.caseId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        dateOfBirth: resolvedDob,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        companyId: resolvedCompanyId
      };
      const res = await CallApiForLoginCheck(loginData);
      if (res?.success) {
        setOtp(["", "", "", "", "", ""]);
        toast.success("OTP resent successfully!");
        inputRefs.current[0]?.focus();
        startCountdown(60);
      }
    } catch {
      setApiError("Failed to Resend OTP.");
    } finally {
      setLoading(false);
    }
  };

  const maskEmail = (email) => {
    if (!email || !email.includes("@")) return "";
    const [name, domainFull] = email.split("@");
    const domainParts = domainFull.split(".");
    const extension = domainParts.pop();
    const domain = domainParts.join(".");
    return `${name.slice(0, 3)}***@***${domain.slice(-3)}.${extension}`;
  };

  const startCountdown = (seconds = 60) => {
    clearInterval(timerRef.current);
    setCountdown(seconds);
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const formatUSPhone = (phone) => {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "").slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const onPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text");
    const digits = pasted.replace(/\D/g, "").slice(0, 10);
    setFormData(prev => ({ ...prev, phoneNumber: digits }));
    if (errors.phoneNumber) setErrors(prev => ({ ...prev, phoneNumber: "" }));
    if (apiError) setApiError("");
  };

  const handleBack = () => {
    setResponse(false);
    setApiError("");
    setOtp(["", "", "", "", "", ""]);
    setCountdown(0);
    clearInterval(timerRef.current);
  };

  const inputClass = (name) => {
    if (errors[name]) return "login-input-error";
    if (focused[name]) return "login-input-focused";
    return "login-input";
  };

  if (showRegisterPage) return <PatientRegistration onSignIn={() => setShowRegisterPage(false)} onBack={() => setShowRegisterPage(false)} />;
  if (showManualLogin) return <ManualLogin onBack={() => setShowManualLogin(false)} />;
  if (!id) return <ManualLogin />;
  if (tokenStatus === "verifying") return <VerifyingScreen />;
  if (tokenStatus === "failed") return <FailedScreen onRetry={handleRetry} />;

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-top-bar" />

        {/* Header */}
        <div className="login-header">
          <div className="login-avatar-box">
            <IconUserAvatar />
          </div>
          <div className="login-header-text">
            <h2 className="login-heading">
              {isPreFilled && formData.firstName
                ? `Welcome! ${formData.firstName}`
                : "Welcome!"}
            </h2>
            <p className="login-subtitle">
              {[formData.doctorName, formData.doctorSpecialtyName, formData.providerName]
                .filter(Boolean)
                .join(" • ")}
            </p>
          </div>
        </div>

        {!response ? (
          <>
            {/* Show pre-filled notice ONLY when token was present and patient is not registered */}
            {isPreFilled && !isRegistered && (
              <div className="login-dob-notice">
                <span className="login-notice-dot" />
                <span>
                  Your details are pre-filled by system. Please confirm your{" "}
                  <strong>DATE OF BIRTH</strong> to proceed.
                </span>
              </div>
            )}

            <form onSubmit={handleLogin} noValidate>

              {!isRegistered && (
                <div className="login-input-row">
                  <div>
                    <label className="login-label">First Name <RequiredStar /></label>
                    <div className="login-input-wrap">
                      <span className="login-input-icon"><IconUser /></span>
                      <input
                        className={inputClass("firstName")}
                        type="text" name="firstName" placeholder="First name"
                        value={formData.firstName} autoComplete="given-name"
                        onChange={handleChange}
                        onFocus={() => setFocused(f => ({ ...f, firstName: true }))}
                        onBlur={() => setFocused(f => ({ ...f, firstName: false }))}
                      />
                    </div>
                    {errors.firstName && <span className="login-error-text">{errors.firstName}</span>}
                  </div>
                  <div>
                    <label className="login-label">Last Name <RequiredStar /></label>
                    <div className="login-input-wrap">
                      <span className="login-input-icon"><IconUser /></span>
                      <input
                        className={inputClass("lastName")}
                        type="text" name="lastName" placeholder="Last name"
                        value={formData.lastName} autoComplete="family-name"
                        onChange={handleChange}
                        onFocus={() => setFocused(f => ({ ...f, lastName: true }))}
                        onBlur={() => setFocused(f => ({ ...f, lastName: false }))}
                      />
                    </div>
                    {errors.lastName && <span className="login-error-text">{errors.lastName}</span>}
                  </div>
                </div>
              )}

              {/* ── Email — disabled when token pre-filled ── */}
              <div className="login-field-group">
                <label className="login-label">Email Address</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><IconMail /></span>
                  <input
                    className={`${inputClass("email")}${isPreFilled ? " login-input-disabled" : ""}`}
                    type="email" name="email" placeholder="Enter your email address"
                    value={formData.email} autoComplete="email" inputMode="email"
                    disabled={isPreFilled}
                    onChange={handleChange}
                    onFocus={() => setFocused(f => ({ ...f, email: true }))}
                    onBlur={() => setFocused(f => ({ ...f, email: false }))}
                  />
                </div>
                {errors.email && <span className="login-error-text">{errors.email}</span>}
              </div>

              {/* ── Phone — disabled when token pre-filled ── */}
              <div className="login-field-group login-field-group--phone">
                <label className="login-label">Phone Number</label>
                <div className="login-input-wrap">
                  <span className="login-input-icon"><IconPhone /></span>
                  <span className="login-phone-prefix">+1</span>
                  <input
                    className={
                      errors.phoneNumber
                        ? "login-input-error login-input-phone-error"
                        : `login-input-phone${isPreFilled ? " login-input-disabled" : ""}`
                    }
                    type="tel" name="phoneNumber" placeholder="10-digit number"
                    value={formatUSPhone(formData.phoneNumber)} inputMode="numeric" autoComplete="tel-national"
                    disabled={isPreFilled}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                      setFormData(prev => ({ ...prev, phoneNumber: digits }));
                      if (errors.phoneNumber) setErrors(prev => ({ ...prev, phoneNumber: "" }));
                      if (apiError) setApiError("");
                    }}
                    onPaste={onPaste}
                    onFocus={() => setFocused(f => ({ ...f, phoneNumber: true }))}
                    onBlur={() => setFocused(f => ({ ...f, phoneNumber: false }))}
                  />
                </div>
                {errors.phoneNumber && <span className="login-error-text">{errors.phoneNumber}</span>}
              </div>

              {!isRegistered && (
                <>
                  <hr className="login-dob-separator" />

                  <div className={`login-field-group login-field-group--dob${formData.dateOfBirth ? " login-field-group--dob-filled" : " login-field-group--dob-empty"}`}>
                    <label className="login-label">
                      Date of Birth <RequiredStar />
                    </label>
                    <LoginDatePicker
                      value={formData.dateOfBirth}
                      onChange={handleDobChange}
                      hasError={!!errors.dateOfBirth}
                    />
                    {errors.dateOfBirth && <span className="login-error-text">{errors.dateOfBirth}</span>}
                  </div>
                </>
              )}

              {apiError && (
                <div className="login-api-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{apiError}</span>
                </div>
              )}

              <button
                type="submit"
                className={`login-btn-primary${loading ? " login-btn-primary--disabled" : ""}`}
                disabled={loading}
              >
                {loading ? "Loading..." : "Proceed to OTP Verification →"}
              </button>

              <div className="login-alt-section">
                <div className="login-divider-wrap">
                  <span className="login-divider-line" />
                  <span className="login-divider-text">or</span>
                  <span className="login-divider-line" />
                </div>

                <button
                  type="button"
                  className="login-alt-tile"
                  onClick={() => {
                    setShowManualLogin(true);
                    setApiError("");
                  }}
                >
                  <div className="login-alt-tile__icon login-alt-tile__icon--switch">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="8.5" cy="7" r="4" />
                      <polyline points="17 8 20 11 23 8" />
                      <line x1="20" y1="11" x2="20" y2="17" />
                    </svg>
                  </div>
                  <div className="login-alt-tile__content">
                    <span className="login-alt-tile__prompt">
                      Not {formData.firstName ? formData.firstName : "you"}?
                    </span>
                    <span className="login-alt-tile__action">Sign in with different details</span>
                  </div>
                  <svg className="login-alt-tile__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>

                <button
                  type="button"
                  className="login-alt-tile"
                  onClick={() => {
                    setShowRegisterPage(true);
                    setApiError("");
                  }}
                >
                  <div className="login-alt-tile__icon login-alt-tile__icon--register">
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="8.5" cy="7" r="4" />
                      <line x1="20" y1="8" x2="20" y2="14" />
                      <line x1="23" y1="11" x2="17" y2="11" />
                    </svg>
                  </div>
                  <div className="login-alt-tile__content">
                    <span className="login-alt-tile__prompt">New here?</span>
                    <span className="login-alt-tile__action">Register as a new patient</span>
                  </div>
                  <svg className="login-alt-tile__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </form>
            <p className="login-footer" style={{ marginTop: 20 }}><IconShield />Your information is encrypted and secure.</p>

          </>

        ) : (
          <>
            <div className="login-otp-icon-wrap">
              <div className="login-otp-icon-circle">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2d8a5e" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
              </div>
            </div>

            <h2 className="login-otp-heading">Verify it's you, {formData.firstName}</h2>
            <p className="login-otp-sub">A 6-digit OTP has been sent to</p>

            <div className="login-otp-contact-cards">
              {formData.phoneNumber && formData.phoneNumber.trim() !== "" && (
                <div className="login-otp-contact-card">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2d8a5e" strokeWidth="2">
                    <rect x="5" y="2" width="14" height="20" rx="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" />
                  </svg>
                  <span className="login-otp-contact-key">Mobile</span>
                  <span className="login-otp-contact-val">+1 ••••••{formData.phoneNumber.slice(-4)}</span>
                </div>
              )}
              {formData.email && formData.email.trim() !== "" && (
                <div className="login-otp-contact-card">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2d8a5e" strokeWidth="2">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <polyline points="2,4 12,13 22,4" />
                  </svg>
                  <span className="login-otp-contact-key">Email</span>
                  <span className="login-otp-contact-val">{maskEmail(formData.email)}</span>
                </div>
              )}
            </div>

            <form onSubmit={handleVerify}>
              <p className="login-otp-code-label">Enter your 6-digit verification code</p>
              <div className="login-otp-row">
                {otp.map((digit, index) => (
                  <React.Fragment key={index}>
                    {/* {index === 3 && <span className="login-otp-dash"></span>} */}
                    <input
                      ref={(el) => (inputRefs.current[index] = el)}
                      type="text" inputMode="numeric"
                      autoComplete={index === 0 ? "one-time-code" : "off"}
                      maxLength={1} value={digit}
                      className={digit ? "login-otp-box--filled" : "login-otp-box"}
                      onChange={(e) => handleChangeOTP(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      onPaste={(e) => handlePaste(e, index)}
                    />
                  </React.Fragment>
                ))}
              </div>
              <div className="login-resend-row">
                {countdown > 0 ? (
                  <span>Resend code in <span className="login-resend-timer">0:{String(countdown).padStart(2, "0")}</span></span>
                ) : (
                  <button type="button" className="login-resend-btn" onClick={handleResendOTP} disabled={loading}>
                    Resend OTP
                  </button>
                )}
              </div>

              {apiError && (
                <div className="login-api-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{apiError}</span>
                </div>
              )}

              <button
                type="submit"
                className={`login-btn-primary${loading || otp.join("").length !== 6 ? " login-btn-primary--disabled" : ""}`}
                disabled={loading || otp.join("").length !== 6}
              >
                {loading ? "Verifying..." : "Verify OTP"}
              </button>
            </form>

            <div className="login-back-btn-wrap">
              <button className="login-back-btn" onClick={handleBack}>
                <IconBack /> Back to Login
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;